import { useState, useEffect, useRef } from 'react';
import { api } from '../utils/api';

const TEAM = ['Ray', 'Adam', 'Diana', 'Bob'];
const STAFF_ORDER = { Ray: 0, Adam: 1, Diana: 2 };
const sortShifts = (shifts) =>
  [...shifts].sort((a, b) => (STAFF_ORDER[a.person] ?? 99) - (STAFF_ORDER[b.person] ?? 99));
const SHIFT_OPTS = ['OFF', 'PTO', 'TM'];
// Staff notes (e.g. why someone is off, or a special assignment) — one per person per day
const shiftNotes = (shifts) =>
  sortShifts((shifts || []).filter(s => s.note && s.note.trim()))
    .filter((s, i, arr) => arr.findIndex(x => x.person === s.person) === i);
const shortName = (p) => (p === 'Diana' ? 'Di' : p);

// Staff filter: a person is "booked" on an entry if they have a shift that isn't time off
const STAFF_FILTERS = ['All', 'Ray', 'Adam', 'Diana'];
const NOT_BOOKED = ['OFF', 'PTO'];
const isBooked = (ev, person) =>
  (ev.shifts || []).some(s => s.person === person && s.shift && !NOT_BOOKED.includes(s.shift.trim().toUpperCase()));

// Tricode lookup: opponent name -> tricode display
const TRICODE = {
  // NHL
  ANA:'ANA',ARI:'ARI',BOS:'BOS',BUF:'BUF',CGY:'CGY',CAR:'CAR',CHI:'CHI',COL:'COL',
  CBJ:'CBJ',DAL:'DAL',DET:'DET',EDM:'EDM',FLA:'FLA',LAK:'LAK',MIN:'MIN',MTL:'MTL',
  NSH:'NSH',NJD:'NJD',NYI:'NYI',NYR:'NYR',OTT:'OTT',PHI:'PHI',PIT:'PIT',SJS:'SJS',
  SEA:'SEA',STL:'STL',TBL:'TBL',TOR:'TOR',UTA:'UTA',VAN:'VAN',VGK:'VGK',WSH:'WSH',WPG:'WPG',
  // MLB
  ARI_MLB:'ARI',ATL:'ATL',BAL:'BAL',BOS_MLB:'BOS',CHC:'CHC',CWS:'CWS',CIN:'CIN',
  CLE:'CLE',COL_MLB:'COL',DET_MLB:'DET',HOU:'HOU',KC:'KC',LAA:'LAA',LAD:'LAD',
  MIA:'MIA',MIL:'MIL',MIN_MLB:'MIN',NYM:'NYM',NYY:'NYY',OAK:'OAK',PHI_MLB:'PHI',
  PIT_MLB:'PIT',SD:'SD',SF:'SF',SEA_MLB:'SEA',STL_MLB:'STL',TB:'TB',TEX:'TEX',
  TOR_MLB:'TOR',WSH_MLB:'WSH',
};
// Simple: just use the stored opponent string directly as tricode (it IS the tricode)
const toTricode = (opp) => opp ? (TRICODE[opp] || opp) : '?';

const NHL_TEAMS = [
  'ANA','ARI','BOS','BUF','CGY','CAR','CHI','COL','CBJ','DAL',
  'DET','EDM','FLA','LAK','MIN','MTL','NSH','NJD','NYI','NYR',
  'OTT','PHI','PIT','SJS','SEA','STL','TBL','TOR','UTA','VAN','VGK','WSH','WPG',
];
const MLB_TEAMS = [
  'ARI','ATL','BAL','BOS','CHC','CWS','CIN','CLE','COL','DET',
  'HOU','KC','LAA','LAD','MIA','MIL','MIN','NYM','NYY','OAK',
  'PHI','PIT','SD','SF','SEA','STL','TB','TEX','TOR','WSH',
];

// Current date in Eastern time, so "today" rolls over at local midnight rather than UTC midnight
const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });

function pad(n) { return String(n).padStart(2, '0'); }

// ── Main CalendarTab ───────────────────────────────────────────────────────

export function CalendarTab({ currentUser, newActionRef }) {
  const [view, setView] = useState('schedule');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editEvent, setEditEvent] = useState(null); // null | 'new' | event obj
  const [viewEvent, setViewEvent] = useState(null); // null | event obj — read-only detail
  const [focusDate, setFocusDate] = useState(today());
  const [monthOffset, setMonthOffset] = useState(0); // months from current
  const [staffFilter, setStaffFilter] = useState('All'); // shared by Schedule and Month views

  // Date range to fetch: current month ± some buffer
  const fetchRange = () => {
    const base = new Date();
    base.setDate(1);
    const from = new Date(base);
    from.setMonth(from.getMonth() - 1);
    // End of the month 4 months out (so e.g. Oct -> end of Feb next year)
    const to = new Date(base);
    to.setMonth(to.getMonth() + 13);
    to.setDate(0); // day 0 = last day of previous month
    return {
      from: from.toISOString().slice(0, 10),
      to: to.toISOString().slice(0, 10),
    };
  };

  const load = async () => {
    setLoading(true);
    try {
      const { from, to } = fetchRange();
      const data = await api.getCalendar(from, to);
      setEvents(data.events || []);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // Expose new event via ref
  useEffect(() => {
    if (newActionRef) newActionRef.current = () => setEditEvent('new');
  }, [newActionRef]);

  const openNew = (date) => setEditEvent({ _new: true, date: date || today() });
  const openEdit = (ev) => setEditEvent(ev);
  const openView = (ev) => setViewEvent(ev);

  const onSaved = () => { setEditEvent(null); load(); };

  const viewBtns = [
    { id: 'schedule', label: '≡ Schedule' },
    { id: 'month',    label: '▦ Month' },
  ];

  return (
    <div style={{ paddingTop: 12 }}>
      {/* View switcher */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {viewBtns.map(v => (
          <button key={v.id} onClick={() => setView(v.id)} style={{
            background: view === v.id ? '#0f172a' : '#e2e8f0',
            color: view === v.id ? '#fff' : '#475569',
            border: 'none', borderRadius: 8, padding: '7px 14px',
            fontWeight: 600, fontSize: 13, cursor: 'pointer',
          }}>{v.label}</button>
        ))}
        <button onClick={() => openNew()} style={{ marginLeft: 'auto', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
          + Add Game
        </button>
      </div>

      {/* Staff filter */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {STAFF_FILTERS.map(name => (
          <button key={name} onClick={() => setStaffFilter(name)} style={{
            background: staffFilter === name ? '#3b82f6' : '#f1f5f9',
            color: staffFilter === name ? '#fff' : '#475569',
            border: 'none', borderRadius: 999, padding: '5px 12px',
            fontWeight: 600, fontSize: 12, cursor: 'pointer',
          }}>{name}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: '#94a3b8', padding: 48 }}>Loading calendar…</div>
      ) : view === 'schedule' ? (
        <ScheduleView events={events} staffFilter={staffFilter} currentUser={currentUser} onView={openView} onNew={openNew} />
      ) : (
        <MonthView events={events} staffFilter={staffFilter} currentUser={currentUser} onView={openView} onNew={openNew} monthOffset={monthOffset} setMonthOffset={setMonthOffset} />
      )}

      {viewEvent && (
        <GameDetailModal
          event={viewEvent}
          onEdit={() => { setEditEvent(viewEvent); setViewEvent(null); }}
          onClose={() => setViewEvent(null)}
        />
      )}

      {editEvent && (
        <EventModal
          event={editEvent === 'new' ? { _new: true, date: today() } : editEvent}
          currentUser={currentUser}
          onSaved={() => { setEditEvent(null); load(); }}
          onClose={() => setEditEvent(null)}
        />
      )}
    </div>
  );
}

// ── Schedule View ─────────────────────────────────────────────────────────

function ScheduleView({ events, staffFilter, currentUser, onView, onNew }) {
  const todayStr = today();
  const [showPast, setShowPast] = useState(false);

  if (events.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0', color: '#94a3b8' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>📅</div>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>No games scheduled yet</div>
        <button onClick={() => onNew()} style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
          Add First Game
        </button>
      </div>
    );
  }

  // Split into past and upcoming
  const visibleEvents = staffFilter === 'All' ? events : events.filter(ev => isBooked(ev, staffFilter));
  const past = visibleEvents.filter(ev => ev.date < todayStr).sort((a, b) => a.date.localeCompare(b.date));
  const upcoming = visibleEvents.filter(ev => ev.date >= todayStr).sort((a, b) => a.date.localeCompare(b.date));

  // Group by month helper
  const groupByMonth = (evs) => {
    const byMonth = {};
    for (const ev of evs) {
      const [y, m] = ev.date.split('-');
      const key = `${y}-${m}`;
      if (!byMonth[key]) byMonth[key] = [];
      byMonth[key].push(ev);
    }
    return byMonth;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {past.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <button onClick={() => setShowPast(p => !p)} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 12, cursor: 'pointer', padding: '8px 0', fontWeight: 600 }}>
            {showPast ? '▾' : '▸'} {past.length} past game{past.length !== 1 ? 's' : ''}
          </button>
          {showPast && Object.entries(groupByMonth(past)).sort(([a],[b]) => a.localeCompare(b)).map(([mon, evs]) => {
            const [y, m] = mon.split('-');
            const label = new Date(Number(y), Number(m) - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
            return (
              <div key={mon}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, padding: '12px 0 6px', borderBottom: '1px solid #e2e8f0', marginBottom: 8 }}>{label}</div>
                {evs.map(ev => <ScheduleRow key={ev.id} ev={ev} currentUser={currentUser} onView={onView} isToday={false} />)}
              </div>
            );
          })}
        </div>
      )}
      {(() => {
        // Group upcoming events by Mon-Sun work week
        const getWeekKey = (dateStr) => {
          const d = new Date(dateStr + 'T12:00:00');
          const dow = d.getDay(); // 0=Sun..6=Sat
          const monday = new Date(d);
          monday.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));
          return monday.toISOString().slice(0, 10);
        };
        const weekMap = {};
        for (const ev of upcoming) {
          const wk = getWeekKey(ev.date);
          if (!weekMap[wk]) weekMap[wk] = [];
          weekMap[wk].push(ev);
        }

        // Week totals use every loaded event, so passed days and the staff filter don't change them
        const weekTotals = {};
        for (const ev of events) {
          const wk = getWeekKey(ev.date);
          if (!weekTotals[wk]) weekTotals[wk] = { Ray: new Set(), Adam: new Set(), Diana: new Set() };
          for (const sh of (ev.shifts || [])) {
            if (weekTotals[wk][sh.person] && sh.shift && sh.shift !== 'OFF') {
              weekTotals[wk][sh.person].add(ev.date);
            }
          }
        }

        // Keep the current week's bar until the week ends, even with no entries left in it
        const currentWk = getWeekKey(todayStr);
        if (weekTotals[currentWk] && !weekMap[currentWk] &&
            (staffFilter === 'All' || weekTotals[currentWk][staffFilter].size > 0)) {
          weekMap[currentWk] = [];
        }
        const weekKeys = Object.keys(weekMap).sort();

        // Build month-grouped rows with week summary bars injected after each week
        const rows = [];
        let lastMonth = null;
        for (const wk of weekKeys) {
          const evs = weekMap[wk].sort((a, b) => a.date.localeCompare(b.date));

          // Days per person for the full Mon-Sun week (shift present and not OFF)
          const totals = weekTotals[wk];
          const rayDays   = totals.Ray.size;
          const adamDays  = totals.Adam.size;
          const dianaDays = totals.Diana.size;
          const showRay   = staffFilter === 'All' || staffFilter === 'Ray';
          const showAdam  = staffFilter === 'All' || staffFilter === 'Adam';
          const showDiana = staffFilter === 'Diana';
          const rayWarn   = showRay && rayDays < 4;
          const adamWarn  = showAdam && adamDays < 4;
          const anyWarn   = rayWarn || adamWarn;

          for (const ev of evs) {
            const mon = ev.date.slice(0, 7);
            if (mon !== lastMonth) {
              const [y, m] = mon.split('-');
              const label = new Date(Number(y), Number(m) - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
              rows.push(
                <div key={'hdr-' + mon} style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, padding: '16px 0 6px', borderBottom: '2px solid #e2e8f0', marginBottom: 8 }}>{label}</div>
              );
              lastMonth = mon;
            }
            rows.push(<ScheduleRow key={ev.id} ev={ev} currentUser={currentUser} onView={onView} isToday={ev.date === todayStr} />);
          }

          // Week summary bar — always shown, warning style if someone is short
          const sunday = new Date(wk + 'T12:00:00');
          sunday.setDate(sunday.getDate() + 6);
          const sunStr = sunday.toISOString().slice(0, 10);
          rows.push(
            <div key={'wk-' + wk} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              margin: '4px 0 10px', padding: '5px 10px',
              borderRadius: 7, fontSize: 11, fontWeight: 600,
              background: anyWarn ? '#fef9c3' : '#f1f5f9',
              border: `1px solid ${anyWarn ? '#fde047' : '#e2e8f0'}`,
            }}>
              <span style={{ color: '#94a3b8', flexShrink: 0 }}>
                Wk of {new Date(wk + 'T12:00:00').toLocaleDateString('default', { month: 'short', day: 'numeric' })}–{sunday.toLocaleDateString('default', { month: 'short', day: 'numeric' })}
              </span>
              {showRay && (
                <span style={{ color: rayWarn ? '#b45309' : '#475569' }}>
                  Ray: {rayDays}d{rayWarn ? ' ⚠' : ''}
                </span>
              )}
              {showRay && showAdam && <span style={{ color: '#cbd5e1' }}>·</span>}
              {showAdam && (
                <span style={{ color: adamWarn ? '#b45309' : '#475569' }}>
                  Adam: {adamDays}d{adamWarn ? ' ⚠' : ''}
                </span>
              )}
              {showDiana && <span style={{ color: '#475569' }}>Diana: {dianaDays}d</span>}
              {anyWarn && <span style={{ color: '#b45309', marginLeft: 'auto' }}>Check schedule</span>}
            </div>
          );
        }
        if (rows.length === 0) {
          return (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8', fontSize: 14 }}>
              {staffFilter === 'All' ? 'No upcoming games scheduled' : `Nothing upcoming for ${staffFilter}`}
            </div>
          );
        }
        return rows;
      })()}
    </div>
  );
}

function ScheduleRow({ ev, currentUser, onView, isToday }) {
  const d = new Date(ev.date + 'T12:00:00');
  const dayNum = d.getDate();
  const dayName = d.toLocaleString('default', { weekday: 'short' });
  const isBoth = ev.team === 'both';

  // Scheduled engineers (non-OFF)
  const scheduled = sortShifts((ev.shifts || []).filter(s => s.shift && s.shift !== 'OFF'));

  return (
    <div
      onClick={() => onView(ev)}
      style={{
        display: 'flex', gap: 12, padding: '12px 14px',
        marginBottom: 6, borderRadius: 10, cursor: 'pointer',
        background: isToday ? '#eff6ff' : '#fff',
        border: `1px solid ${isToday ? '#bfdbfe' : '#e2e8f0'}`,
        boxShadow: '0 1px 3px rgba(0,0,0,.04)',
        transition: 'background .1s',
      }}
    >
      {/* Date blob */}
      <div style={{ width: 44, flexShrink: 0, textAlign: 'center', paddingTop: 2 }}>
        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>{dayName}</div>
        <div style={{ fontSize: 24, fontWeight: 800, color: isToday ? '#2563eb' : '#0f172a', lineHeight: 1.1 }}>{dayNum}</div>
      </div>

      {/* Game info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {ev.day_type === 'offday' ? (
          <div style={{ fontSize: 16, fontWeight: 800, color: '#64748b', letterSpacing: -0.3, marginBottom: 3 }}>OFF DAY</div>
        ) : ev.day_type === 'event' ? (
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#0e7490', letterSpacing: -0.3, marginBottom: 2 }}>
              📌 {ev.game1_opponent || 'Event'}{ev.game1_time ? ' · ' + ev.game1_time : ''}
            </div>
            {ev.day_note && <div style={{ fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>{ev.day_note}</div>}
          </div>
        ) : isBoth ? (
          <>
            <GameLine slot={1} ev={ev} />
            <GameLine slot={2} ev={ev} />
          </>
        ) : (
          <GameLine slot={1} ev={ev} />
        )}

        {/* Engineer roster */}
        {scheduled.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
            {scheduled.map((s, i) => (
              <span key={i} style={{ fontSize: 12, color: '#475569' }}>
                <strong>{s.person}</strong> {s.shift}
              </span>
            ))}
          </div>
        )}

        {/* Staff notes */}
        {shiftNotes(ev.shifts).map((s, i) => (
          <div key={i} style={{ fontSize: 12, color: '#7c2d12', marginTop: 3 }}>
            <strong>{s.person}:</strong> {s.note}
          </div>
        ))}

        {ev.day_note && ev.day_type !== 'event' ? <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, fontStyle: 'italic' }}>{ev.day_note}</div> : null}
      </div>
    </div>
  );
}

function GameLine({ slot, ev }) {
  const opp = slot === 1 ? ev.game1_opponent : ev.game2_opponent;
  const loc = slot === 1 ? ev.game1_location : ev.game2_location;
  const time = slot === 1 ? ev.game1_time : ev.game2_time;
  const bcast = slot === 1 ? ev.game1_broadcaster : ev.game2_broadcaster;

  if (!opp) return null;

  const isBoth = ev.team === 'both';
  const teamForSlot = isBoth ? (slot === 1 ? 'bucs' : 'pens') : ev.team;
  const teamLabel = teamForSlot === 'pens' ? 'PENS' : 'BUCS';
  const isHome = (loc || '').toLowerCase() === 'home';
  const connector = isHome ? 'vs' : '@';
  const hasSpecial = slot === 1 && ['altcast', 'bucsbp'].includes((ev.special_type || '').toLowerCase());

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 3 }}>
      <span style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', letterSpacing: -0.3 }}>
        {teamLabel} {connector} {opp}
      </span>
      {loc && !hasSpecial && <LocBadge loc={loc} nonSnp={!!(bcast && !bcast.includes('SNP'))} />}
      {slot === 1 && <SpecialBadge type={ev.special_type} />}
      {time && <span style={{ fontSize: 12, color: '#64748b' }}>{time}</span>}
      {bcast && <span style={{ fontSize: 12, fontWeight: bcast.includes('SNP') ? 700 : 400, color: bcast.includes('SNP') ? '#0f172a' : '#64748b' }}>{bcast}</span>}
    </div>
  );
}

function LocBadge({ loc, nonSnp }) {
  const map = {
    home:  { label: 'HOME',  bg: '#eab308', color: '#000000' },
    cloud: { label: 'CLOUD', bg: '#16a34a', color: '#ffffff' },
    away:  { label: 'AWAY',  bg: '#dc2626', color: '#ffffff' },
  };
  const key = (loc || '').toLowerCase();
  let s = map[key] || { label: loc.toUpperCase(), bg: '#f1f5f9', color: '#475569' };
  if (nonSnp && key === 'home') s = { label: 'HOME', bg: '#dc2626', color: '#ffffff' };
  return <span style={{ fontSize: 10, fontWeight: 700, background: s.bg, color: s.color, borderRadius: 4, padding: '2px 5px', letterSpacing: .3 }}>{s.label}</span>;
}

function SpecialBadge({ type }) {
  if (!type) return null;
  const map = {
    altcast: { label: 'ALTCAST', bg: '#7c3aed', color: '#ffffff' },
    bucsbp:  { label: 'BUCS BP', bg: '#1d4ed8', color: '#ffffff' },
  };
  const s = map[(type || '').toLowerCase()];
  if (!s) return null;
  return <span style={{ fontSize: 10, fontWeight: 700, background: s.bg, color: s.color, borderRadius: 4, padding: '2px 5px', letterSpacing: .3 }}>{s.label}</span>;
}

// ── Month View ─────────────────────────────────────────────────────────────

function MonthView({ events, staffFilter, currentUser, onView, onNew, monthOffset, setMonthOffset }) {
  const base = new Date();
  base.setDate(1);
  base.setMonth(base.getMonth() + monthOffset);
  const year = base.getFullYear();
  const month = base.getMonth();
  const label = base.toLocaleString('default', { month: 'long', year: 'numeric' });

  const firstDow = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Build event map by date string
  const eventMap = {};
  for (const ev of events) {
    if (!eventMap[ev.date]) eventMap[ev.date] = [];
    eventMap[ev.date].push(ev);
  }

  const todayStr = today();
  const cells = [];
  // Leading blanks
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const ds = `${year}-${pad(month + 1)}-${pad(d)}`;
    cells.push({ d, ds, evs: eventMap[ds] || [] });
  }
  // Pad to complete rows
  while (cells.length % 7 !== 0) cells.push(null);

  const DOW = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  return (
    <div>
      {/* Nav */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <button onClick={() => setMonthOffset(o => o - 1)} style={navBtn}>‹</button>
        <span style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>{label}</span>
        <button onClick={() => setMonthOffset(o => o + 1)} style={navBtn}>›</button>
      </div>

      {/* DOW headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 2 }}>
        {DOW.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#94a3b8', padding: '4px 0' }}>{d}</div>
        ))}
      </div>

      {/* Cells */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((cell, i) => {
          if (!cell) return <div key={`blank-${i}`} style={{ minHeight: 64, background: '#f8fafc', borderRadius: 8 }} />;
          const { d, ds, evs: allEvs } = cell;
          const evs = staffFilter === 'All' ? allEvs : allEvs.filter(ev => isBooked(ev, staffFilter));
          // A filtered-out entry still opens on click, so an empty-looking day can't create a duplicate
          const clickTarget = evs[0] || allEvs[0];
          const isToday = ds === todayStr;
          const hasPens = evs.some(e => e.team === 'pens' || e.team === 'both');
          const hasBucs = evs.some(e => e.team === 'bucs' || e.team === 'both');

          return (
            <div
              key={ds}
              onClick={() => clickTarget ? onView(clickTarget) : onNew(ds)}
              style={{
                minHeight: 80, maxHeight: 110, overflow: 'hidden',
                background: isToday ? '#eff6ff' : '#fff',
                border: `1px solid ${isToday ? '#bfdbfe' : '#e2e8f0'}`,
                borderRadius: 8, padding: '4px 5px', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', gap: 2,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: isToday ? 800 : 500, color: isToday ? '#2563eb' : '#475569', lineHeight: 1 }}>{d}</div>
              {evs.map(ev => <MonthEventChip key={ev.id} ev={ev} />)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MonthEventChip({ ev }) {
  const isBoth = ev.team === 'both';
  const isPens = ev.team === 'pens' || ev.team === 'both';
  const isBucs = ev.team === 'bucs' || ev.team === 'both';
  const isOff  = ev.day_type === 'offday';

  // Engineers scheduled (sorted by priority)
  const scheduled = sortShifts((ev.shifts || []).filter(s => s.shift && s.shift !== 'OFF'));
  const names = scheduled.map(s => s.person === 'Diana' ? 'Di' : s.person);
  const notes = shiftNotes(ev.shifts);
  const notesBlock = notes.length > 0 ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1, paddingLeft: 2 }}>
      {notes.map((s, i) => (
        <div key={i} title={`${s.person}: ${s.note}`} style={{ fontSize: 9, color: '#7c2d12', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <strong>{shortName(s.person)}:</strong> {s.note}
        </div>
      ))}
    </div>
  ) : null;

  if (isOff) {
    if (names.length === 0 && notes.length === 0) return null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{ background: '#fef08a', borderRadius: 4, padding: '2px 4px', lineHeight: 1.4 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#0f172a' }}>OFF DAY</div>
        </div>
        {names.length > 0 && (
          <div style={{ fontSize: 9, color: '#475569', fontWeight: 600, lineHeight: 1.2, paddingLeft: 2 }}>
            {names.join(' · ')}
          </div>
        )}
        {notesBlock}
      </div>
    );
  }

  if (ev.day_type === 'event') {
    const evName = ev.game1_opponent || 'Event';
    const evTime = (() => {
      const t = ev.game1_time || '';
      if (!t) return '';
      if (t.includes(' PM')) return t.replace(':00','').replace(' PM','pm');
      if (t.includes(' AM')) return t.replace(':00','').replace(' AM','am');
      return t.replace(':00','');
    })();
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{ background: '#cffafe', borderRadius: 4, padding: '2px 4px', lineHeight: 1.4 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            📌 {evName}{evTime ? ' ' + evTime : ''}
          </div>
          {ev.day_note && (
            <div style={{ fontSize: 9, color: '#0e7490', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ev.day_note}</div>
          )}
        </div>
        {names.length > 0 && (
          <div style={{ fontSize: 9, color: '#475569', fontWeight: 600, lineHeight: 1.2, paddingLeft: 2 }}>{names.join(' · ')}</div>
        )}
        {notesBlock}
      </div>
    );
  }

  const pensColor = '#fef08a';
  const bucsColor = '#bbf7d0';

  const GameChip = ({ slot }) => {
    const opp   = slot === 1 ? ev.game1_opponent   : ev.game2_opponent;
    const time  = slot === 1 ? ev.game1_time        : ev.game2_time;
    const loc   = slot === 1 ? ev.game1_location    : ev.game2_location;
    const bcast = slot === 1 ? ev.game1_broadcaster : ev.game2_broadcaster;
    const teamId = isBoth ? (slot === 1 ? 'pens' : 'bucs') : ev.team;
    const myCode = teamId === 'pens' ? 'PIT' : 'PGH';
    const bg = teamId === 'pens' ? pensColor : bucsColor;
    const locLower = (loc || '').toLowerCase();
    const isHome = locLower === 'home';
    const isCloud = locLower === 'cloud';
    const connector = isHome ? 'vs' : '@';
    const oppCode = toTricode(opp);
    const shortTime = (() => {
      if (!time) return '';
      if (time.includes(' PM')) return time.replace(':00','').replace(' PM','pm');
      if (time.includes(' AM')) return time.replace(':00','').replace(' AM','am');
      // Already short format like "7:00" or "7:30"
      return time.replace(':00','');
    })();
    const locLabel = isHome ? 'Home' : isCloud ? 'Cloud' : loc ? 'Away' : '';
    const net = bcast ? bcast.split(' & ')[0] : '';
    const specialLabel = ev.special_type === 'altcast' ? 'ALTCAST' : ev.special_type === 'bucsbp' ? 'BUCS BP' : null;
    const specialBg    = ev.special_type === 'altcast' ? '#7c3aed' : ev.special_type === 'bucsbp' ? '#1d4ed8' : null;
    return (
      <div style={{
        background: ev.special_type === 'altcast' ? '#ede9fe' : ev.special_type === 'bucsbp' ? '#dbeafe' : bg,
        borderRadius: 4, padding: '2px 4px', lineHeight: 1.4,
      }}>
        {specialLabel && (
          <div style={{ fontSize: 8, fontWeight: 800, color: '#fff', background: specialBg, borderRadius: 3, padding: '0px 3px', display: 'inline-block', marginBottom: 1, letterSpacing: 0.5 }}>
            {specialLabel}
          </div>
        )}
        <div style={{ fontSize: 10, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {myCode} {connector} {oppCode}{shortTime ? ' ' + shortTime : ''}
        </div>
        {(locLabel || net) && (
          <div style={{ fontSize: 9, color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {locLabel}{locLabel && net ? ' · ' : ''}{net}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <GameChip slot={1} />
      {isBoth && <GameChip slot={2} />}
      {names.length > 0 && (
        <div style={{ fontSize: 9, color: '#475569', fontWeight: 600, lineHeight: 1.2, paddingLeft: 2 }}>
          {names.join(' · ')}
        </div>
      )}
      {notesBlock}
    </div>
  );
}

const navBtn = { background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '7px 14px', fontWeight: 700, fontSize: 18, cursor: 'pointer', color: '#0f172a' };

// ── Game Detail Modal (read-only) ─────────────────────────────────────────

function GameDetailModal({ event: ev, onEdit, onClose }) {
  const isBoth = ev.team === 'both';
  const d = new Date(ev.date + 'T12:00:00');
  const dateLabel = d.toLocaleString('default', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  const renderGame = (slot) => {
    const opp     = slot === 1 ? ev.game1_opponent   : ev.game2_opponent;
    const time    = slot === 1 ? ev.game1_time        : ev.game2_time;
    const loc     = slot === 1 ? ev.game1_location    : ev.game2_location;
    const bcast   = slot === 1 ? ev.game1_broadcaster : ev.game2_broadcaster;
    const teamForSlot = isBoth ? (slot === 1 ? 'bucs' : 'pens') : ev.team;
    const teamLabel = teamForSlot === 'pens' ? 'PENS' : 'BUCS';
    const isHome = (loc || '').toLowerCase() === 'home';
    const connector = isHome ? 'vs' : '@';

    if (!opp) return null;

    return (
      <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px', marginBottom: 10 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>
          {teamLabel} {connector} {opp}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {loc && <LocBadge loc={loc} nonSnp={!!(bcast && !bcast.includes('SNP'))} />}
          {slot === 1 && <SpecialBadge type={ev.special_type} />}
          {time && <span style={{ fontSize: 13, color: '#475569' }}>🕐 {time}</span>}
          {bcast && (
            <span style={{ fontSize: 13, fontWeight: bcast.includes('SNP') ? 700 : 400, color: '#0f172a' }}>
              📺 {bcast}
            </span>
          )}
        </div>
      </div>
    );
  };

  const scheduled = sortShifts((ev.shifts || []).filter(s => s.shift && s.shift !== 'OFF'));
  const offAll    = (ev.shifts || []).filter(s => !s.shift || s.shift === 'OFF');
  const detailNotes = shiftNotes(ev.shifts);
  const off       = offAll.filter(s => !detailNotes.some(n => n.person === s.person));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 150, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 12px', overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 500, boxShadow: '0 8px 32px rgba(0,0,0,.2)' }}>
        {/* Header */}
        <div style={{ background: '#0f172a', color: '#fff', padding: '14px 18px', borderRadius: '14px 14px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{dateLabel}</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 22, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Game(s) */}
          {isBoth ? (
            <>
              {renderGame(1)}
              {renderGame(2)}
            </>
          ) : renderGame(1)}

          {/* Engineer schedule */}
          <div>
            <div style={lbl}>Engineer Schedule</div>
            {scheduled.length === 0 ? (
              <div style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>No one assigned yet</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {scheduled.map((s, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                    <span style={{ fontWeight: 600, color: '#0f172a' }}>{s.person}</span>
                    <span style={{ color: s.shift === 'PTO' ? '#9333ea' : s.shift === 'TM' ? '#0369a1' : '#15803d', fontWeight: 600 }}>{s.shift}</span>
                  </div>
                ))}
                {off.length > 0 && (
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                    OFF: {off.map(s => s.person).join(', ')}
                  </div>
                )}
              </div>
            )}
            {detailNotes.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
                {detailNotes.map((s, i) => (
                  <div key={i} style={{ fontSize: 13, color: '#7c2d12' }}>
                    <strong>{s.person}:</strong> {s.note}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Day note */}
          {ev.day_note && (
            <div>
              <div style={lbl}>Notes</div>
              <div style={{ fontSize: 14, color: '#334155', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{ev.day_note}</div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
            <button onClick={onClose} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '9px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 14, color: '#334155' }}>Close</button>
            <button onClick={onEdit} style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>✏️ Edit</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Event Form Modal ───────────────────────────────────────────────────────

function EventModal({ event, currentUser, onSaved, onClose }) {
  const isNew = !!event._new;

  const [date, setDate] = useState(event.date || today());
  const [team, setTeam] = useState(event.team || 'pens');
  const [dayNote, setDayNote] = useState(event.day_note || '');
  const [specialType, setSpecialType] = useState(event.special_type || '');
  const [dayType, setDayType] = useState(event.day_type || '');

  // Game 1
  const [g1opp, setG1opp] = useState(event.game1_opponent || '');
  const [g1time, setG1time] = useState(event.game1_time || '');
  const [g1loc, setG1loc] = useState(event.game1_location || '');
  const [g1bcast, setG1bcast] = useState(event.game1_broadcaster || '');
  const [g1bcast2, setG1bcast2] = useState(''); // simulcast slot
  const [g1simulcast, setG1simulcast] = useState(event.game1_broadcaster?.includes(' & ') || false);

  // Game 2 (crossover)
  const [g2opp, setG2opp] = useState(event.game2_opponent || '');
  const [g2time, setG2time] = useState(event.game2_time || '');
  const [g2loc, setG2loc] = useState(event.game2_location || '');
  const [g2bcast, setG2bcast] = useState(event.game2_broadcaster || '');
  const [g2simulcast, setG2simulcast] = useState(event.game2_broadcaster?.includes(' & ') || false);
  const [g2bcast2, setG2bcast2] = useState('');

  // Shifts: { person: { slot: shiftString } }
  const initShifts = () => {
    const s = {};
    for (const p of TEAM) {
      s[p] = { 1: '', 2: '' };
    }
    for (const sh of (event.shifts || [])) {
      if (!s[sh.person]) s[sh.person] = { 1: '', 2: '' };
      s[sh.person][sh.game_slot || 1] = sh.shift || '';
    }
    return s;
  };
  const [shifts, setShifts] = useState(initShifts);
  // Staff notes: { person: noteString } — stored on the person's slot-1 shift row
  const [staffNotes, setStaffNotes] = useState(() => {
    const n = {};
    for (const sh of (event.shifts || [])) {
      if (sh.note && !n[sh.person]) n[sh.person] = sh.note;
    }
    return n;
  });

  const [saving, setSaving] = useState(false);

  const setShift = (person, slot, val) => {
    setShifts(s => ({ ...s, [person]: { ...s[person], [slot]: val } }));
  };

  const isBoth = team === 'both';
  const oppList = (isBoth || team === 'pens') ? NHL_TEAMS : (team === 'bucs') ? MLB_TEAMS : [];

  const save = async () => {
    setSaving(true);
    const id = event.id || `ev_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

    const bcast1 = g1simulcast && g1bcast2 ? `${g1bcast} & ${g1bcast2}` : g1bcast;
    const bcast2 = g2simulcast && g2bcast2 ? `${g2bcast} & ${g2bcast2}` : g2bcast;

    const ev = {
      id, date, team,
      game1_opponent: g1opp, game1_time: g1time, game1_location: g1loc, game1_broadcaster: bcast1,
      game2_opponent: g2opp, game2_time: g2time, game2_location: g2loc, game2_broadcaster: bcast2,
      day_note: dayNote,
      special_type: specialType || null,
      day_type: dayType || null,
      created_by: event.created_by || currentUser,
    };

    const shiftRows = [];
    for (const person of TEAM) {
      const s1 = shifts[person]?.[1];
      const s2 = shifts[person]?.[2];
      const note = s1 === 'OFF' ? (staffNotes[person] || '').trim() : '';
      if (s1) shiftRows.push({ id: `sh_${id}_${person}_1`, event_id: id, person, game_slot: 1, shift: s1, note });
      if (isBoth && s2) shiftRows.push({ id: `sh_${id}_${person}_2`, event_id: id, person, game_slot: 2, shift: s2 });
    }

    try {
      await api.saveCalendarEvent(ev, shiftRows);
      onSaved();
    } catch (e) {
      alert('Save failed. Please try again.');
    }
    setSaving(false);
  };

  const del = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Delete this game entry?')) return;
    await api.deleteCalendarEvent(event.id);
    onSaved();
  };

  // Quick-select broadcaster chips
  const bcastChips = ['SNP', 'ESPN', 'ESPN2', 'TNT', 'ABC', 'NHLN', 'MLB.tv'];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '16px 12px', overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 520, marginBottom: 32, boxShadow: '0 8px 32px rgba(0,0,0,.2)' }}>
        {/* Header */}
        <div style={{ background: '#0f172a', color: '#fff', padding: '14px 18px', borderRadius: '14px 14px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>{isNew ? 'Add Game' : 'Edit Game'}</span>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>

        <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Date */}
          <div>
            <div style={lbl}>Date</div>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inp} />
          </div>

          {/* Day Type toggles */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span
                onClick={() => setDayType(d => d === 'offday' ? '' : 'offday')}
                style={{ ...chipStyle2, background: dayType === 'offday' ? '#64748b' : '#f1f5f9', color: dayType === 'offday' ? '#fff' : '#334155', padding: '6px 14px', fontSize: 13, fontWeight: 700 }}
              >📅 OFF DAY</span>
              <span
                onClick={() => setDayType(d => d === 'event' ? '' : 'event')}
                style={{ ...chipStyle2, background: dayType === 'event' ? '#0891b2' : '#f1f5f9', color: dayType === 'event' ? '#fff' : '#334155', padding: '6px 14px', fontSize: 13, fontWeight: 700 }}
              >📌 EVENT</span>
              {dayType === 'offday' && <span style={{ fontSize: 12, color: '#94a3b8' }}>No game — staff schedule only</span>}
            </div>
          </div>

          {/* Event fields */}
          {dayType === 'event' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, background: '#f0f9ff', borderRadius: 10, padding: '12px 14px', border: '1px solid #bae6fd' }}>
              <div>
                <div style={lbl}>Event Name</div>
                <input value={g1opp} onChange={e => setG1opp(e.target.value)} placeholder="e.g. AHN Studio Meeting" style={inp} />
              </div>
              <div>
                <div style={lbl}>Time</div>
                <GameTimeSelect value={g1time} onChange={setG1time} times={SHIFT_TIMES} />
              </div>
              <div>
                <div style={lbl}>Note</div>
                <input value={dayNote} onChange={e => setDayNote(e.target.value)} placeholder="Optional details" style={inp} />
              </div>
            </div>
          )}

          {/* Team */}
          <div style={{ opacity: (dayType === 'offday' || dayType === 'event') ? 0.35 : 1, pointerEvents: (dayType === 'offday' || dayType === 'event') ? 'none' : 'auto' }}>
            <div style={lbl}>Team</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[{id:'pens',label:'🐧 Penguins'},{id:'bucs',label:'⚾ Pirates'},{id:'both',label:'🔀 Both'}].map(t => (
                <span key={t.id} onClick={() => setTeam(t.id)}
                  style={{ ...chipStyle2, background: team === t.id ? '#0f172a' : '#f1f5f9', color: team === t.id ? '#fff' : '#334155', flex: 1, textAlign: 'center' }}
                >{t.label}</span>
              ))}
            </div>
          </div>

          {/* Game 1 */}
          <div style={{ opacity: (dayType === 'offday' || dayType === 'event') ? 0.35 : 1, pointerEvents: (dayType === 'offday' || dayType === 'event') ? 'none' : 'auto' }}>
          <GameSection
            slot={1}
            team={isBoth ? 'bucs' : team}
            label={isBoth ? 'Pirates Game' : 'Game'}
            opp={g1opp} setOpp={setG1opp}
            time={g1time} setTime={setG1time}
            loc={g1loc} setLoc={setG1loc}
          />
          <div>
            <div style={lbl}>Broadcaster (Game {isBoth ? '1 – Pirates' : ''})</div>
            <div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                {bcastChips.map(ch => (
                  <span key={ch} onClick={() => setG1bcast(v => v === ch ? '' : ch)}
                    style={{ ...chipStyle2, background: g1bcast === ch ? '#0f172a' : '#f1f5f9', color: g1bcast === ch ? '#fff' : '#334155' }}
                  >{ch}</span>
                ))}
                <span onClick={() => setG1simulcast(s => !s)}
                  style={{ ...chipStyle2, background: g1simulcast ? '#7c3aed' : '#f1f5f9', color: g1simulcast ? '#fff' : '#334155' }}
                >+ Simulcast</span>
              </div>
              <input value={g1bcast} onChange={e => setG1bcast(e.target.value)} placeholder="Broadcaster (free text)" style={inp} />
              {g1simulcast && <input value={g1bcast2} onChange={e => setG1bcast2(e.target.value)} placeholder="Second broadcaster" style={{ ...inp, marginTop: 4 }} />}
            </div>
          </div>

          {/* Game 2 — crossover only */}
          {isBoth && (
            <>
              <div style={{ borderTop: '1px dashed #e2e8f0', paddingTop: 12 }}>
                <GameSection
                  slot={2}
                  team="pens"
                  label="Penguins Game"
                  opp={g2opp} setOpp={setG2opp}
                  time={g2time} setTime={setG2time}
                  loc={g2loc} setLoc={setG2loc}
                />
                <div style={{ marginTop: 12 }}>
                  <div style={lbl}>Broadcaster (Game 2 – Penguins)</div>
                  <div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                      {bcastChips.map(ch => (
                        <span key={ch} onClick={() => setG2bcast(v => v === ch ? '' : ch)}
                          style={{ ...chipStyle2, background: g2bcast === ch ? '#0f172a' : '#f1f5f9', color: g2bcast === ch ? '#fff' : '#334155' }}
                        >{ch}</span>
                      ))}
                      <span onClick={() => setG2simulcast(s => !s)}
                        style={{ ...chipStyle2, background: g2simulcast ? '#7c3aed' : '#f1f5f9', color: g2simulcast ? '#fff' : '#334155' }}
                      >+ Simulcast</span>
                    </div>
                    <input value={g2bcast} onChange={e => setG2bcast(e.target.value)} placeholder="Broadcaster (free text)" style={inp} />
                    {g2simulcast && <input value={g2bcast2} onChange={e => setG2bcast2(e.target.value)} placeholder="Second broadcaster" style={{ ...inp, marginTop: 4 }} />}
                  </div>
                </div>
              </div>
            </>
          )}

          </div>

          {/* Shifts */}
          <div>
            <div style={{ ...lbl, marginBottom: 10 }}>Engineer Shifts</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {TEAM.map(person => (
                <div key={person} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div style={{ width: 52, fontSize: 13, fontWeight: 600, color: '#334155' }}>{person}</div>
                  <ShiftInput
                    value={shifts[person]?.[1] || ''}
                    onChange={v => setShift(person, 1, v)}
                    label={isBoth ? 'PIT' : ''}
                  />
                  {isBoth && (
                    <ShiftInput
                      value={shifts[person]?.[2] || ''}
                      onChange={v => setShift(person, 2, v)}
                      label="PGH"
                    />
                  )}
                </div>
                {shifts[person]?.[1] === 'OFF' && (
                  <input
                    value={staffNotes[person] || ''}
                    onChange={e => setStaffNotes(n => ({ ...n, [person]: e.target.value }))}
                    placeholder={`Note for ${person} (optional) — e.g. jury duty, Steelers remote`}
                    style={{ ...inp, fontSize: 13, padding: '6px 10px', marginLeft: 60, width: 'calc(100% - 60px)' }}
                  />
                )}
                </div>
              ))}
            </div>
          </div>

          {/* Special Type */}
          <div>
            <div style={lbl}>Special Designation (optional)</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[{id:'altcast',label:'ALTCAST',bg:'#7c3aed'},{id:'bucsbp',label:'BUCS BP',bg:'#1d4ed8'}].map(t => (
                <span key={t.id} onClick={() => setSpecialType(s => s === t.id ? '' : t.id)}
                  style={{ ...chipStyle2, background: specialType === t.id ? t.bg : '#f1f5f9', color: specialType === t.id ? '#fff' : '#334155', flex: 1, textAlign: 'center' }}
                >{t.label}</span>
              ))}
            </div>
          </div>

          {/* Day note */}
          <div>
            <div style={lbl}>Day Note (optional)</div>
            <input value={dayNote} onChange={e => setDayNote(e.target.value)} placeholder="Studio show, notes, etc." style={inp} />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 4 }}>
            {!isNew ? (
              <button onClick={del} style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 8, padding: '9px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>Delete</button>
            ) : <div />}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onClose} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '9px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 14, color: '#334155' }}>Cancel</button>
              <button onClick={save} disabled={saving} style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: saving ? .6 : 1 }}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const GAME_TIMES = [
  '12:00 PM','12:30 PM','1:00 PM','1:30 PM','2:00 PM','3:00 PM','3:30 PM',
  '4:00 PM','5:00 PM','6:00 PM','6:30 PM','7:00 PM','7:30 PM','8:00 PM','8:30 PM',
];
const SHIFT_TIMES = [
  '5:00 AM','5:30 AM','6:00 AM','6:30 AM','7:00 AM','7:30 AM','8:00 AM','8:30 AM',
  '9:00 AM','9:30 AM','10:00 AM','10:30 AM','11:00 AM','11:30 AM',
  '12:00 PM','12:30 PM','1:00 PM','1:30 PM','2:00 PM','3:00 PM',
];

const selectStyle = { border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none', background: '#fff', cursor: 'pointer' };

function GameTimeSelect({ value, onChange, times }) {
  const timeList = times || GAME_TIMES;
  const isCustom = value && !timeList.includes(value);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <select value={isCustom ? '__custom__' : (value || '')} onChange={e => {
        if (e.target.value === '__custom__') onChange('');
        else onChange(e.target.value);
      }} style={selectStyle}>
        <option value=''>-- select time --</option>
        {timeList.map(t => <option key={t} value={t}>{t}</option>)}
        <option value='__custom__'>Custom…</option>
      </select>
      {(isCustom || value === '') && (
        <input value={isCustom ? value : ''} onChange={e => onChange(e.target.value)}
          placeholder='type time (e.g. 2:05 PM)' style={{ ...selectStyle, fontSize: 13 }} />
      )}
    </div>
  );
}

function ShiftTimeSelect({ value, onChange }) {
  const isCustom = value && !SHIFT_TIMES.includes(value);
  return (
    <select value={isCustom ? value : (value || '')} onChange={e => onChange(e.target.value)} style={{ ...selectStyle, padding: '5px 8px', fontSize: 12 }}>
      <option value=''>-- select --</option>
      {SHIFT_TIMES.map(t => <option key={t} value={t}>{t}</option>)}
      {isCustom && <option value={value}>{value}</option>}
    </select>
  );
}

function GameSection({ slot, team, label, opp, setOpp, time, setTime, loc, setLoc }) {
  const oppList = team === 'bucs' ? MLB_TEAMS : NHL_TEAMS;
  return (
    <div>
      <div style={lbl}>{label}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Opponent */}
        <div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Opponent</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
            {oppList.map(t => (
              <span key={t} onClick={() => setOpp(o => o === t ? '' : t)}
                style={{ ...chipStyle2, background: opp === t ? '#0f172a' : '#f1f5f9', color: opp === t ? '#fff' : '#334155', padding: '3px 8px', fontSize: 11 }}
              >{t}</span>
            ))}
          </div>
          <input value={opp} onChange={e => setOpp(e.target.value)} placeholder="or type opponent" style={inp} />
        </div>

        {/* Time + Location */}
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Game Time</div>
            <GameTimeSelect value={time} onChange={setTime} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Location</div>
            <div style={{ display: 'flex', gap: 5 }}>
              {['home','cloud','away'].map(l => (
                <span key={l} onClick={() => setLoc(x => x === l ? '' : l)}
                  style={{ ...chipStyle2, background: loc === l ? '#0f172a' : '#f1f5f9', color: loc === l ? '#fff' : '#334155', flex: 1, textAlign: 'center', fontSize: 11 }}
                >{l}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ShiftInput({ value, onChange, label }) {
  const [custom, setCustom] = useState(!SHIFT_OPTS.includes(value) && value !== '');
  const [raw, setRaw] = useState(value);

  useEffect(() => {
    if (!SHIFT_OPTS.includes(value) && value !== '') {
      setCustom(true);
      setRaw(value);
    }
  }, [value]);

  return (
    <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
      {label && <span style={{ fontSize: 10, color: '#94a3b8', width: 22, flexShrink: 0 }}>{label}</span>}
      {custom ? (
        <div style={{ display: 'flex', gap: 4, flex: 1, alignItems: 'center' }}>
          <ShiftTimeSelect value={raw} onChange={v => { setRaw(v); onChange(v); }} />
          <span onClick={() => { setCustom(false); setRaw(''); onChange(''); }}
            style={{ ...chipStyle2, padding: '3px 7px', fontSize: 11, background: '#fee2e2', color: '#dc2626', flexShrink: 0 }}
          >✕</span>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 3, flex: 1 }}>
          {SHIFT_OPTS.map(o => (
            <span key={o} onClick={() => onChange(o === value ? '' : o)}
              style={{ ...chipStyle2, flex: 1, textAlign: 'center', fontSize: 11, background: value === o ? '#0f172a' : '#f1f5f9', color: value === o ? '#fff' : '#334155' }}
            >{o}</span>
          ))}
          <span onClick={() => setCustom(true)}
            style={{ ...chipStyle2, flex: 1.2, textAlign: 'center', fontSize: 11, background: '#e0f2fe', color: '#0369a1' }}
          >Time…</span>
        </div>
      )}
    </div>
  );
}

const lbl = { fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: .5 };
const inp = { border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 14, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' };
const chipStyle2 = { display: 'inline-block', padding: '5px 10px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer', userSelect: 'none' };

